import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  TuiButtonModule,
  TuiDialogService,
  TuiHintModule,
  TuiSvgModule,
} from '@taiga-ui/core';
import {
  catchError,
  combineLatest,
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  of,
  Subject,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { AlertService } from '@shared/services/alert.service';
import { AsyncPipe, DatePipe } from '@angular/common';
import {
  TUI_PROMPT,
  TuiAccordionItemComponent,
  TuiAccordionModule,
} from '@taiga-ui/kit';
import { Store } from '@ngxs/store';
import { TuiLetModule, tuiPure } from '@taiga-ui/cdk';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';
import { CargoCatalogsService } from '@shared/services/cargo-catalogs.service';
import { countPlacesAndSumPackages } from '@shared/utils/cargo-calculation';
import { CtrlClickDirective } from '@shared/directives/ctrl-click.directive';
import { CtrlShiftDirective } from '@shared/directives/shift-click.directive';
import { AvatarComponent } from '@shared/components/avatar/avatar.component';
import { AuthService } from '@shared/services/auth.service';
import { AuthState } from '@store/auth/auth.state';
import {
  TuiBadgedContentModule,
  TuiBadgeNotificationModule,
} from '@taiga-ui/experimental';
import { PolymorpheusComponent } from '@tinkoff/ng-polymorpheus';
import { PreOrderService } from '../../services/pre-order.service';
import { PreOrdersState } from '../../store/pre-orders.state';
import { PreOrder } from '../../../../types/pre-order';
import { PreOrderComponent } from '../pre-order/pre-order.component';
import { CommonStateService } from '../../services/common-state.service';
import { OrganizationUser } from '../../../../types/auth';

import {
  PreOrderAttachmentsComponent,
  PreOrderAttachmentsInputData,
} from '../pre-order/components/pre-order-attachments/pre-order-attachments.component';

type PreparedPreOrder = {
  original: PreOrder;
  computed: {
    flowerTypeName: string;
    places: string;
    packages: number;
  };
};
@Component({
  selector: 'app-pre-orders',
  standalone: true,
  imports: [
    TuiButtonModule,
    AsyncPipe,
    TuiAccordionModule,
    PreOrderComponent,
    TuiLetModule,
    TuiSvgModule,
    CtrlClickDirective,
    CtrlShiftDirective,
    AvatarComponent,
    TuiHintModule,
    TuiBadgeNotificationModule,
    TuiBadgedContentModule,
    DatePipe,
  ],
  templateUrl: './pre-orders.component.html',
  styleUrl: './pre-orders.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PreOrdersComponent implements OnInit {
  @ViewChildren(TuiAccordionItemComponent, { read: TuiAccordionItemComponent })
  accordionItems: QueryList<TuiAccordionItemComponent>;

  @ViewChild(PreOrderComponent, { read: PreOrderComponent })
  openedPreOrderComponentRef?: PreOrderComponent;

  create$ = new Subject<void>();
  delete$ = new Subject<number>();
  copy$ = new Subject<number>();

  openAttachmentsDialog$ = new Subject<{
    preOrder: PreOrder;
    index: number;
  }>();

  preOrders$ = this.store.select(PreOrdersState.getPreOrders);

  preparedPreOrders$: Observable<PreparedPreOrder[]> = combineLatest([
    this.preOrders$,
  ]).pipe(
    map(([preOrders]) => {
      const flowerTypesMap = this.catalogs.flowerTypesMap;
      return preOrders.map(preOrder => {
        const { packages, places } = countPlacesAndSumPackages(preOrder);

        return {
          original: preOrder,
          computed: {
            places,
            packages,
            flowerTypeName:
              flowerTypesMap.get(preOrder.flowerType ?? '')?.name ?? '',
          },
        };
      });
    }),
  );

  snowflakeUrl = 'assets/icons/snowflake.svg';
  user$ = this.store.select(AuthState.getUser);
  users$ = this.user$.pipe(
    switchMap(user =>
      this.auth.getAllUserByDivisionId(user?.activeDivisionId ?? 0),
    ),
  );

  constructor(
    private readonly preOrderService: PreOrderService,
    private readonly alert: AlertService,
    private readonly store: Store,
    private readonly catalogs: CargoCatalogsService,
    protected readonly commonStateService: CommonStateService,
    private readonly destroyRef: DestroyRef,
    protected readonly sanitizer: DomSanitizer,
    private readonly dialogs: TuiDialogService,
    private readonly auth: AuthService,
    private readonly injector: Injector,
  ) {}

  onCreatePreOrder() {
    this.create$
      .pipe(
        switchMap(() => {
          return this.preOrderService.create();
        }),
        delay(0),
        tap(() => {
          this.openLastPreOrder();
        }),
        catchError(() => {
          this.alert.showError();
          return of(null);
        }),
      )
      .subscribe();
  }

  onDeletePreOrder() {
    this.delete$
      .pipe(
        switchMap(id =>
          this.dialogs
            .open<boolean>(TUI_PROMPT, {
              data: {
                content:
                  '<h1 class="font-bold text-lg">Удалить предзаказ ?<h1>',
                yes: 'Подтвердить',
                no: 'Отмена',
              },
            })
            .pipe(
              filter(Boolean),
              map(() => id),
            ),
        ),
        switchMap(id => {
          return this.preOrderService.deleteById(id);
        }),
        catchError(() => {
          this.alert.showError();
          return of(null);
        }),
      )
      .subscribe();
  }

  onCopyPreOrder() {
    this.copy$
      .pipe(
        switchMap(id => {
          return this.preOrderService.copy(id);
        }),
        delay(0),
        tap(() => {
          this.openLastPreOrder();
        }),
        catchError(() => {
          this.alert.showError();
          return of(null);
        }),
      )
      .subscribe();
  }

  onCtrlClick() {
    return this.onCtrlClick$.pipe(
      tap(item => {
        this.commonStateService.selectItems(item);
      }),
    );
  }

  onShiftClick() {
    return this.onShiftClick$.pipe(
      withLatestFrom(this.preOrders$),
      tap(([item, items]) => {
        this.commonStateService.selectAllUntil(item, items);
      }),
    );
  }

  onAttachments() {
    this.openAttachmentsDialog$
      .pipe(
        switchMap(({ preOrder, index }) => {
          const openedPreOrderComponentForm =
            this.openedPreOrderComponentRef?.preOrderForm.getRawValue();

          const latestOrderValue =
            openedPreOrderComponentForm?.id === preOrder.id
              ? openedPreOrderComponentForm
              : preOrder;

          const data: PreOrderAttachmentsInputData = {
            preOrder: latestOrderValue,
            canEdit: !!latestOrderValue.prepayment,
            canRemove: true,
            canUpload: !!latestOrderValue.prepayment,
          };

          return this.dialogs
            .open<PreOrder>(
              new PolymorpheusComponent(
                PreOrderAttachmentsComponent,
                this.injector,
              ),
              {
                size: 'auto',
                data,
              },
            )
            .pipe(
              map(updatedPreOrder => {
                return {
                  updatedPreOrder,
                  preOrder,
                  index,
                };
              }),
            );
        }),
        withLatestFrom(this.preOrders$),
        switchMap(([{ updatedPreOrder }]) => {
          return this.preOrderService.update(updatedPreOrder).pipe(
            tap(() => {
              this.alert.showSuccess();
            }),
          );
        }),
        catchError(err => {
          this.alert.showError(err.error);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onShiftClick$ = new Subject<PreOrder>();
  onCtrlClick$ = new Subject<PreOrder>();
  @tuiPure
  findUserById(users: OrganizationUser[], userId: Nullable<number>) {
    return users.find(u => u.id === userId);
  }

  openLastPreOrder() {
    const component = this.accordionItems.last;

    const clickEvent = new MouseEvent('click', {
      view: window,
      bubbles: true,
      cancelable: false,
    });
    component?.nativeFocusableElement?.dispatchEvent(clickEvent);
  }

  fetchPreOrders() {
    merge(
      this.commonStateService.weekNumber.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
      ),
      this.commonStateService.selectedSortingFields$,
      this.commonStateService.refresh$,
    )
      .pipe(
        withLatestFrom(this.commonStateService.selectedSortingFields$),
        switchMap(([, fields]) => {
          const weekNumber =
            this.commonStateService.weekNumber.getRawValue() ?? undefined;
          const sorting = fields.map(({ value }) => value);
          return this.preOrderService.getAll(sorting, weekNumber);
        }),
        tap(() => this.commonStateService.clearSelectedItems()),
        catchError(() => {
          this.alert.showError();
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnInit() {
    this.fetchPreOrders();
    this.onCreatePreOrder();
    this.onDeletePreOrder();
    this.onCopyPreOrder();
    this.onAttachments();
    merge(this.onCtrlClick(), this.onShiftClick())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }
}
